import { Fragment, useEffect, useState } from "react";

import { Info, ArrowBack } from "@mui/icons-material";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LinkIcon from "@mui/icons-material/Link";
import {
  Box,
  Button,
  Chip,
  Divider,
  Link,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { alpha, lighten } from "@mui/material/styles";
import {
  getBulletinShare,
  postBulletinShareAdmit,
  postBulletinShareReject,
} from "@rubi/api-generated";
import { BulletinShareWithSubresourcesResource } from "@rubi/api-generated/clientPy/types.gen";
import { colors } from "@rubi/ui";
import { toast } from "@rubi/ui";
import { Trans, useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import "react-loading-skeleton/dist/skeleton.css";

import { EmailButton, PhoneButton } from "@/common";
import {
  formatDateLong,
  formatDateShort,
  formatDateWithoutTime,
} from "@/common/utils";
import styles from "@/components/alerts/add/DisplayAlert/DisplayAlert.module.scss";
import { PublishedResolution } from "@/components/alerts/add/DisplayAlert/ResolveBulletin";
import AlertMediaSlider from "@/components/alerts/AlertMediaSlider";
import { LOCATION_CLASSIFICATION_REFERENCE } from "@/components/alerts/constants";
import {
  getAddressClassification,
  getAddressDescription,
} from "@/components/alerts/utils";
import { usePoliceServiceNameImage } from "@/hooks/useCommunities";
import {
  IBulletinResolution,
  IEvidenceFile,
  ITag,
  PlaceType,
} from "@/types/typesAlerts";

interface BulletinShareProps {
  shareId?: string;
  onAdmit?: (id: number) => void;
  onReject?: (id: number) => void;
}

export const BulletinShare = ({
  shareId: shareIdProp,
  onAdmit,
  onReject,
}: BulletinShareProps = {}) => {
  const policeServiceNameImage = usePoliceServiceNameImage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { shareId: shareIdParam } = useParams();
  const shareId = shareIdProp ?? shareIdParam;
  const [shareData, setShareData] =
    useState<BulletinShareWithSubresourcesResource | null>(null);
  const { t, i18n } = useTranslation([
    "bulletin",
    "bulletin/bulletinType",
    "bulletin/classification",
  ]);
  const locale = i18n.language;
  const [lang, setLang] = useState("en");
  const theme = useTheme();
  const isSmall = useMediaQuery((theme as Theme).breakpoints.down("sm"));
  const [openTooltip, setOpenTooltip] = useState(false);

  const handleTooltipToggle = () => {
    setOpenTooltip(!openTooltip);
  };

  const iconStyle = {
    color: "darkblue",
    cursor: "pointer",
    marginLeft: 1,
    fontSize: isSmall ? 20 : 14,
  };

  const renderContactInformation = (contacts) => {
    return (
      <>
        {contacts
          .filter((contact) => contact.name)
          .map((item, index) => (
            <div style={{ whiteSpace: "pre-wrap" }} key={index}>
              {item?.name ? item.name : ""}
              {item?.name && item?.badge_number ? " - " : ""}
              {item?.badge_number ? item.badge_number : ""}
              {item?.name || item?.badge_number ? "     " : ""}
              {item.email !== "" && (
                <EmailButton
                  email={item.email}
                  subject={`Inquiry about ${t(`bulletin/bulletinType:${shareData?.bulletin?.type}`)} alert - ${shareData?.bulletin?.occurrence_number?.join()}`}
                />
              )}
              {item.phone_number !== "" && (
                <>
                  {" "}
                  <PhoneButton
                    phoneNumber={item.phone_number}
                    extension={item.extension}
                  />
                </>
              )}
              {index < contacts.length - 1 && <br />}
            </div>
          ))}
      </>
    );
  };

  const languageToggleForBulletin = () => {
    // Only show language toggle if bulletin is in French
    if (shareData?.bulletin?.original_language !== "fr") return null;
    return (
      <div className="mb-2 text-right fs-sm">
        {lang === "fr" ? (
          <div>
            View{" "}
            <a
              style={{
                color: "blue",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => setLang("en")}
            >
              English
            </a>{" "}
            Bulletin
          </div>
        ) : (
          <div>
            This Bulletin is originally in{" "}
            {shareData?.bulletin?.original_language}, View{" "}
            <a
              style={{
                color: "blue",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => setLang("fr")}
            >
              French
            </a>{" "}
            Bulletin
          </div>
        )}
      </div>
    );
  };

  const mode = "view";
  const isSelectedAlert =
    searchParams.get("bulletinId") === shareData?.bulletin?.id?.toString();

  let policeLogo;
  const psName = shareData?.bulletin?.issuing_police_service?.name;
  if (psName && policeServiceNameImage[psName]) {
    policeLogo = policeServiceNameImage[psName];
  } else {
    policeLogo = policeServiceNameImage["DRDC"];
  }

  const publishedResolutions = (
    shareData?.bulletin?.resolutions as IBulletinResolution[] | null | undefined
  )?.filter((resolution) => resolution.published);

  useEffect(() => {
    if (!shareId || shareId === "") return;
    getBulletinShare({ path: { bulletin_share_id: Number(shareId) } })
      .then((res) => setShareData(res.data ?? null))
      .catch((err) => toast.error(err?.message ?? String(err)));
  }, [shareId]);

  const incidentTimeText = (incidentTime: {
    start_date: string;
    end_date: string;
    time_type: string;
  }) => {
    if (incidentTime.time_type === "range") {
      return `${formatDateShort(incidentTime.start_date, locale)} - ${formatDateShort(incidentTime.end_date, locale)}`;
    } else if (incidentTime.time_type === "unknown") {
      return `${t("bulletin:time_unknown")}`;
    } else if (incidentTime.time_type === "exact") {
      return `${formatDateLong(incidentTime.start_date, locale)}`;
    } else if (incidentTime.time_type === "not_applicable") {
      return `${t("bulletin:not_applicable")}`;
    } else if (incidentTime.time_type === "time_unknown") {
      return formatDateWithoutTime(incidentTime.start_date, locale);
    }
  };

  const linkToMapView = (addressText: string, addressIndex: number) => {
    if (mode === "view") {
      window.open(
        `https://maps.google.com/maps/place/${encodeURIComponent(addressText)}`,
        "_blank",
      );
      return;
    }

    navigate(
      `/alerts?bulletinId=${shareData?.bulletin?.id}&view=map&addressIndex=${addressIndex}`,
    );
  };

  const isStandalone = !onAdmit && !onReject;

  const handleStandaloneAdmit = async () => {
    try {
      await postBulletinShareAdmit({
        path: { bulletin_share_id: parseInt(shareId!) },
      });
      toast.success("Bulletin share admitted successfully");
      navigate(`/rubialert/bulletin-share`);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to admit bulletin share",
      );
    }
  };

  const handleStandaloneReject = async () => {
    try {
      await postBulletinShareReject({
        path: { bulletin_share_id: parseInt(shareId!) },
      });
      toast.success("Bulletin share rejected successfully");
      navigate(`/rubialert/bulletin-share`);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to reject bulletin share",
      );
    }
  };

  return (
    <div className="alert-container">
      {isStandalone && shareData && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1.5,
              px: 3,
              py: 1.5,
              bgcolor: alpha(theme.palette.warning.light, 0.4),
              boxShadow: 6,
              alignSelf: "stretch",
            }}
          >
            <Button
              startIcon={<ArrowBack />}
              size="small"
              sx={{
                mr: "auto",
                textTransform: "none",
                color: "text.primary",
              }}
              onClick={() => navigate("/rubialert/bulletin-share")}
            >
              Back
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{
                textTransform: "uppercase",
                borderColor: alpha(theme.palette.common.black, 0.23),
                color: "text.primary",
              }}
              startIcon={<CancelOutlinedIcon />}
              onClick={handleStandaloneReject}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              size="small"
              sx={{
                textTransform: "uppercase",
                bgcolor: colors.warning.main,
                "&:hover": { bgcolor: colors.warning.dark },
              }}
              startIcon={<CheckCircleOutlineIcon />}
              onClick={handleStandaloneAdmit}
            >
              Approve
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 0.75,
              bgcolor: lighten(theme.palette.warning.light, 0.8),
              alignSelf: "stretch",
            }}
          >
            <Typography variant="body2" sx={{ fontSize: 13 }}>
              {shareData.source_user?.rubi_user?.first_name}{" "}
              {shareData.source_user?.rubi_user?.last_name}
            </Typography>
            <Box component="span">→</Box>
            <Typography variant="body2" sx={{ fontSize: 13 }}>
              {shareData.destination_group?.name}
            </Typography>
          </Box>
          <Divider sx={{ alignSelf: "stretch" }} />
        </>
      )}
      {shareData?.bulletin && (
        <div
          className={styles.alert_preview_container + " mb-2 "}
          style={{ marginTop: "2rem" }}
        >
          {publishedResolutions
            ?.sort(
              (a, b) =>
                new Date((b as any).time_updated).getTime() -
                new Date((a as any).time_updated).getTime(),
            )
            .map((resolution) => (
              <div
                key={resolution.id}
                className={styles.publishedResolutions + " w-100"}
              >
                <PublishedResolution resolution={resolution} />
              </div>
            ))}

          <div className={styles.header_container}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "darkblue", textAlign: "center" }}
              >
                ***{" "}
                {t(
                  `bulletin/classification:${shareData.bulletin.classification}`,
                )}{" "}
                ***
              </Typography>
              <Tooltip
                title={
                  <Fragment>
                    <span>
                      <Trans
                        t={t}
                        i18nKey={`bulletin/classification:${shareData.bulletin.classification}_description`}
                        components={{ b: <b /> }}
                      />
                    </span>{" "}
                    <Link
                      href="https://www.tbs-sct.canada.ca/pol/doc-eng.aspx?id=32614"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("bulletin:read_more_here")}
                    </Link>
                  </Fragment>
                }
                placement={isSmall ? "bottom" : "right"}
                onClose={() => setOpenTooltip(false)}
                open={openTooltip}
                arrow
              >
                <Info sx={iconStyle} onClick={handleTooltipToggle} />
              </Tooltip>
            </div>

            <div className={styles.alert_heading_inner1}>
              <div>
                <img
                  src={policeLogo}
                  className={styles.police_logo}
                  alt="Police logo"
                />
              </div>

              <div className={styles.type_crimes_container}>
                <Typography
                  align="center"
                  variant={isSmall ? "h5" : "h4"}
                  style={{ textTransform: "uppercase" }}
                >
                  <b>
                    {t(`bulletin/bulletinType:${shareData.bulletin.type}`) || (
                      <Skeleton />
                    )}
                  </b>
                </Typography>
              </div>
              <div></div>
            </div>

            {shareData.bulletin.incident_times.map((time, index) => {
              return (
                <Typography variant="subtitle1" align="center" key={index}>
                  <b>{t("bulletin:incident_time", { index: index + 1 })}</b>{" "}
                  {incidentTimeText(time as any)}
                </Typography>
              );
            })}
          </div>

          <div
            className="px-md-4 py-1 my-1 mx-auto"
            style={{
              maxWidth: "var(--page-max-width)",
            }}
          >
            <AlertMediaSlider
              medias={(
                Object.values(
                  shareData.bulletin.evidence_files,
                ) as IEvidenceFile[]
              ).sort((a, b) => a?.sort_ordinal - b?.sort_ordinal)}
              entities={shareData.bulletin.entities}
              handleSlideClick={() => {}}
            />
          </div>

          <div
            className={styles.bulletin_container + " mx-2 mx-md-0 mx-auto px-3"}
          >
            {languageToggleForBulletin()}
            {shareData.bulletin.tags &&
              Object.keys(shareData.bulletin.tags).length > 0 && (
                <>
                  <Grid container spacing={2}>
                    {Object.values(
                      shareData.bulletin.tags as { [key: string]: ITag },
                    ).map(
                      (tag) =>
                        tag.description !== "" && (
                          <Grid
                            size={{ xs: 12, md: 6 }}
                            key={tag.id.toString()}
                          >
                            <Tooltip title={tag.title}>
                              <Chip
                                label={tag.title}
                                color="secondary"
                                style={{
                                  height: "24px",
                                  maxWidth: "160px",
                                }}
                              />
                            </Tooltip>
                            <Typography
                              variant="body1"
                              style={{ whiteSpace: "pre-line" }}
                              paragraph
                            >
                              {lang === "fr"
                                ? (tag.description_fr ?? tag.description)
                                : tag.description}
                            </Typography>
                          </Grid>
                        ),
                    )}
                  </Grid>
                </>
              )}

            <div style={{ display: "grid" }}>
              {(shareData.bulletin.location as any)?.addresses?.map(
                (address: PlaceType, index: number) => {
                  const classification =
                    getAddressClassification(address) ||
                    LOCATION_CLASSIFICATION_REFERENCE["Incident Address"];
                  const isDuplicate =
                    (
                      (shareData.bulletin!.location as any)
                        .addresses as PlaceType[]
                    ).filter(
                      (addr) =>
                        getAddressClassification(addr) === classification,
                    ).length > 1;
                  const isMapView = searchParams.get("view") === "map";
                  return (
                    <Typography
                      variant="body1"
                      key={index}
                      className={styles.alertAddressLine}
                    >
                      {isMapView ? (
                        <span
                          className={`${styles.alertAddressPin} ${isSelectedAlert ? styles.selectedAlertAddressPin : styles.compareAlertAddressPin}`}
                        >
                          {index + 1}
                        </span>
                      ) : (
                        ""
                      )}
                      {classification}
                      {isDuplicate ? ` ${index + 1}` : ""}:{" "}
                      {!shareData.bulletin!.is_draft ? (
                        <a
                          className={styles.alertAddressHover}
                          onClick={() =>
                            linkToMapView(getAddressDescription(address), index)
                          }
                        >
                          <b>{getAddressDescription(address)}</b>
                          <LinkIcon
                            sx={{ marginLeft: "8px", verticalAlign: "middle" }}
                            fontSize="small"
                          />
                        </a>
                      ) : (
                        <b>{getAddressDescription(address)}</b>
                      )}
                    </Typography>
                  );
                },
              )}
            </div>

            <br />
            <Typography
              variant="body1"
              style={{ whiteSpace: "pre-line" }}
              paragraph
            >
              {shareData.bulletin.description}
            </Typography>
            <Typography variant="h6">
              {t("bulletin:contact_info_header")}
            </Typography>

            <div style={{ whiteSpace: "pre-line" }}>
              {renderContactInformation(shareData.bulletin.contacts)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
